/**
 * Utilitaires partagés pour le contrat cadre (Master Agreement).
 * Alignés avec FrameworkContractsView et la logique backend getCanonicalMasterAgreement.
 */

import { MA_DOCUMENT_TITLE } from '@/constants/mission'

/**
 * True si le document est le Contrat Cadre (MA).
 * Priorité au flag backend is_master_agreement ; fallback id/titre pour anciennes réponses.
 * @param {Object} doc - Document mission (id, title, isMasterAgreement)
 * @returns {boolean}
 */
export function isMasterAgreementDocument(doc) {
  if (!doc) return false
  if (doc.isMasterAgreement === true || doc.is_master_agreement === true) return true
  if (doc.isMasterAgreement === false || doc.is_master_agreement === false) return false
  if (String(doc.id || '').startsWith('master_agreement_')) return true
  if (doc.title === MA_DOCUMENT_TITLE) return true
  if (doc.title && String(doc.title).toLowerCase().includes('contrat cadre')) return true
  return false
}

/**
 * Statut du contrat cadre : résiliation explicite (DB) vs échéance naturelle, puis attente de signature.
 * valid_until : timestamptz ISO (backend) ou date seule YYYY-MM-DD (legacy).
 * terminated_at / status terminated|rejected : fin anticipée → `terminated` (distinct de l’expiration calendaire).
 * @param {Object} contract - { valid_until, pending_signature, terminated_at, status }
 * @returns {'terminated'|'expired'|'pending-signature'|'active'|null}
 */
export function getMasterAgreementStatus(contract) {
  if (!contract) return null
  if (contract.terminated_at || contract.status === 'terminated' || contract.status === 'rejected') {
    return 'terminated'
  }
  if (!contract.valid_until) return null
  const raw = contract.valid_until
  const now = new Date()
  let expired = false
  if (String(raw).match(/^\d{4}-\d{2}-\d{2}$/)) {
    // Date seule (legacy) : expiré si aujourd'hui (Paris) >= valid_until
    const todayParis = getTodayParisDateString()
    expired = todayParis >= raw
  } else {
    // ISO timestamptz : expiré si now >= valid_until
    expired = new Date(raw) <= now
  }
  if (expired) return 'expired'
  if (contract.pending_signature) return 'pending-signature'
  return 'active'
}

/** Date du jour en Europe/Paris au format YYYY-MM-DD (pour comparaison sans décalage). */
function getTodayParisDateString() {
  const formatter = new Intl.DateTimeFormat('fr-CA', { timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit' })
  const parts = formatter.formatToParts(new Date())
  const y = parts.find((p) => p.type === 'year').value
  const m = parts.find((p) => p.type === 'month').value
  const d = parts.find((p) => p.type === 'day').value
  return `${y}-${m}-${d}`
}

/**
 * Le freelance a déjà un contrat cadre réutilisable (actif ou en attente de signature, pas expiré).
 * Utilisé pour sauter l'étape contrat cadre dans le flow création mission.
 * Si le MA est expiré, on ne saute pas : il faut proposer l'étape pour en créer un nouveau.
 * @param {Object} freelancer - objet freelancer (liste ou détail) avec has_valid_master_agreement et/ou master_agreement
 * @returns {boolean}
 */
export function hasExistingMasterAgreement(freelancer) {
  if (!freelancer?.master_agreement) return Boolean(freelancer?.has_valid_master_agreement)
  const status = getMasterAgreementStatus(freelancer.master_agreement)
  return status === 'active' || status === 'pending-signature'
}
